import React from "react";
import { Box, Flex, Text, Button, Icon, useColorModeValue } from "@chakra-ui/react";

/**
 * Hook returning the light/dark-aware token set used across the inspector,
 * keeping every sub-component consistent and legible in both color modes.
 * @returns {Object} Named color tokens.
 */
export function useInspectorTokens() {
    return {
        label: useColorModeValue("secondaryGray.600", "secondaryGray.400"),
        value: useColorModeValue("secondaryGray.900", "white"),
        border: useColorModeValue("secondaryGray.200", "whiteAlpha.200"),
        tileBg: useColorModeValue("secondaryGray.300", "whiteAlpha.100"),
        accent: useColorModeValue("brand.500", "brand.400"),
    };
}

/**
 * Uppercase section label.
 * @param {Object} props - { children }
 * @returns {React.ReactElement}
 */
export function SectionTitle({ children }) {
    const { label } = useInspectorTokens();
    return (
        <Text color={label} fontSize="0.66rem" fontWeight="700" textTransform="uppercase"
            letterSpacing="0.6px" mb="6px">{children}</Text>
    );
}

/**
 * Compact key/value row (label left, value right) with a hairline separator.
 * @param {Object} props - { label, value, valueColor }
 * @returns {React.ReactElement}
 */
export function KvRow({ label, value, valueColor }) {
    const t = useInspectorTokens();
    return (
        <Flex justify="space-between" gap="14px" py="5px" borderBottom="1px solid" borderColor={t.border}>
            <Text fontSize="0.72rem" color={t.label} flexShrink={0}>{label}</Text>
            <Text fontSize="0.76rem" fontWeight="600" textAlign="right" wordBreak="break-word"
                color={valueColor || t.value}>{value ?? "—"}</Text>
        </Flex>
    );
}

/**
 * Small stat tile (big number + caption) for at-a-glance metrics.
 * @param {Object} props - { label, value, accent, icon }
 * @returns {React.ReactElement}
 */
export function StatTile({ label, value, accent, icon }) {
    const t = useInspectorTokens();
    return (
        <Box p="8px 10px" borderRadius="12px" bg={t.tileBg} border="1px solid" borderColor={t.border}>
            <Flex align="center" gap="5px" mb="2px">
                {icon && <Icon as={icon} boxSize="13px" color={accent || t.accent} />}
                <Text fontSize="0.6rem" fontWeight="700" textTransform="uppercase" letterSpacing="0.4px" color={t.label}>{label}</Text>
            </Flex>
            <Text fontSize="1.05rem" fontWeight="800" lineHeight="1.1" color={t.value}>{value}</Text>
        </Box>
    );
}

/**
 * Summary row with a label, a status/value, and a drill-down action button
 * that opens the full (existing) management component in a drawer.
 * @param {Object} props - { icon, label, value, valueColor, actionLabel, onAction }
 * @returns {React.ReactElement}
 */
export function ManageRow({ icon, label, value, valueColor, actionLabel, onAction }) {
    const t = useInspectorTokens();
    return (
        <Flex align="center" gap="10px" py="9px" borderBottom="1px solid" borderColor={t.border}>
            <Icon as={icon} boxSize="18px" color={t.accent} flexShrink={0} />
            <Box flex="1" minW={0}>
                <Text fontSize="0.78rem" fontWeight="600" color={t.value}>{label}</Text>
                {value != null && <Text fontSize="0.7rem" color={valueColor || t.label} isTruncated>{value}</Text>}
            </Box>
            <Button size="xs" variant="outline" colorScheme="brand" borderRadius="8px" flexShrink={0} onClick={onAction}>
                {actionLabel}
            </Button>
        </Flex>
    );
}

/**
 * Muted centered hint for empty data.
 * @param {Object} props - { children }
 * @returns {React.ReactElement}
 */
export function EmptyHint({ children }) {
    const { label } = useInspectorTokens();
    return <Text py="14px" textAlign="center" color={label} fontSize="0.78rem">{children}</Text>;
}
